import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { BookingFormInline } from './BookingFormInline';
import { RatingPrompt } from './RatingPrompt';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ChatBot = () => {
  const { t, language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [sessionStartTime] = useState(() => Date.now());
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [showRatingPrompt, setShowRatingPrompt] = useState(false);
  const [ratingTrigger, setRatingTrigger] = useState(null);
  const [messageCount, setMessageCount] = useState(0);
  const [hasRated, setHasRated] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Add welcome message when chat opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          role: 'assistant',
          content: t('chat.welcome'),
        },
      ]);
    }
  }, [isOpen, t, messages.length]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      console.log('📤 Sending message:', userMessage);
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: userMessage,
        language: language,
      });

      console.log('📥 Response received:', response.data);

      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: response.data.response },
      ]);
      
      // Check if we should show booking form
      if (response.data.show_booking_form) {
        console.log('📋 Showing booking form with data:', response.data.booking_data);
        setBookingData(response.data.booking_data || {});
        setShowBookingForm(true);
      } else {
        console.log('ℹ️ No booking form trigger detected');
      }
      
      // Check if we should show rating prompt
      if (response.data.show_rating_prompt && !hasRated) {
        console.log('⭐ Showing rating prompt:', response.data.rating_trigger);
        setRatingTrigger(response.data.rating_trigger);
        setMessageCount(response.data.message_count || 0);
        
        // Store AI-detected sentiment for submission
        if (response.data.user_sentiment) {
          sessionStorage.setItem(`sentiment_${sessionId}`, response.data.user_sentiment);
          sessionStorage.setItem(`sentiment_score_${sessionId}`, response.data.sentiment_score || '5');
          console.log(`🧠 AI Sentiment: ${response.data.user_sentiment} (${response.data.sentiment_score}/10)`);
        }
        
        // Delay showing rating to not overwhelm user
        setTimeout(() => {
          setShowRatingPrompt(true);
        }, 1000);
      }
    } catch (error) {
      console.error('❌ Chat error:', error);
      console.error('Error details:', error.response?.data || error.message);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'I apologize, but I encountered an error. Please try again.' },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const submitRating = async (ratingData) => {
    try {
      console.log('⭐ Submitting rating:', ratingData);
      
      // Calculate conversation duration in seconds
      const conversationDuration = Math.floor((Date.now() - sessionStartTime) / 1000);
      
      // Retrieve AI-detected sentiment from storage
      const detectedSentiment = sessionStorage.getItem(`sentiment_${sessionId}`);
      const sentimentScore = sessionStorage.getItem(`sentiment_score_${sessionId}`);
      
      const payload = {
        session_id: sessionId,
        rating: ratingData.rating,
        emoji: ratingData.emoji,
        comment: ratingData.comment,
        trigger_type: ratingTrigger,
        message_count: messageCount,
        conversation_duration: conversationDuration,
        user_sentiment: detectedSentiment || null,  // AI-detected sentiment
        language: language,
      };
      
      console.log('📊 Rating payload with AI sentiment:', payload);
      
      await axios.post(`${API}/chat/rating`, payload);
      
      console.log('✅ Rating submitted successfully');
      setShowRatingPrompt(false);
      setHasRated(true);
      
      // Clean up sentiment data
      sessionStorage.removeItem(`sentiment_${sessionId}`);
      sessionStorage.removeItem(`sentiment_score_${sessionId}`);
      
      // Show thank you message
      const thankYouMsg = language === 'ar'
        ? 'شكراً لتقييمك! نقدر ملاحظاتك. 🙏'
        : language === 'fr'
        ? 'Merci pour votre évaluation! Nous apprécions vos commentaires. 🙏'
        : 'Thank you for your rating! We appreciate your feedback. 🙏';
      
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: thankYouMsg },
      ]);
    } catch (error) {
      console.error('❌ Error submitting rating:', error);
      // Silently fail - don't disturb user experience
    }
  };

  const closeRatingPrompt = () => {
    setShowRatingPrompt(false);
    console.log('ℹ️ User closed rating prompt');
  };

  const handleKeyPress = (e) => {
    // Ctrl+Enter or Cmd+Enter to send message
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      sendMessage();
    }
    // Just Enter adds a new line (default behavior)
  };

  // Auto-resize textarea as user types
  const handleInputChange = (e) => {
    setInput(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  };

  return (
    <>
      {/* Chat Button with Pulsating Ring */}
      <div className={`fixed bottom-6 right-6 z-50 ${isOpen ? 'hidden' : ''}`}>
        {/* "Can I help you?" Label */}
        <motion.div
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.5, duration: 0.5 }}
          className="absolute right-20 top-1/2 -translate-y-1/2 bg-white px-4 py-2 rounded-lg shadow-lg whitespace-nowrap"
        >
          <p className="text-sm font-sans text-[#0F1F3C] font-medium">Can I help you?</p>
          <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-l-[8px] border-l-white"></div>
        </motion.div>
        
        {/* Pulsating Gold Ring */}
        <div className="absolute inset-0 w-16 h-16">
          <span className="absolute inset-0 rounded-full border-2 border-[#D4AF37] animate-ping opacity-75"></span>
          <span className="absolute inset-[-4px] rounded-full border-2 border-[#D4AF37] animate-pulse"></span>
        </div>
        
        {/* Button */}
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 1, type: 'spring' }}
          onClick={() => setIsOpen(true)}
          className="relative w-16 h-16 rounded-full shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
          data-testid="chatbot-toggle"
          aria-label="Open chat"
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_frenchhotelscape/artifacts/a1c0cpip_image.png" 
            alt="Concierge"
            className="w-full h-full rounded-full object-cover border-[3px] border-[#D4AF37]"
          />
        </motion.button>
      </div>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] bg-white shadow-2xl flex flex-col overflow-hidden"
            style={{ height: '600px', maxHeight: 'calc(100vh - 48px)' }}
            data-testid="chatbot-window"
          >
            {/* Header */}
            <div className="bg-[#0F1F3C] text-white p-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_frenchhotelscape/artifacts/a1c0cpip_image.png" 
                  alt="Concierge"
                  className="w-12 h-12 rounded-full object-cover border-2 border-[#D4AF37]"
                />
                <div>
                  <h3 className="font-serif text-lg">{t('chat.title')}</h3>
                  <p className="text-white/60 text-xs font-sans">{t('chat.subtitle')}</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 flex items-center justify-center hover:bg-white/10 rounded transition-colors"
                data-testid="chatbot-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#F9F8F6]">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start items-end space-x-2'}`}
                >
                  {message.role === 'assistant' && (
                    <img 
                      src="https://customer-assets.emergentagent.com/job_frenchhotelscape/artifacts/a1c0cpip_image.png" 
                      alt="Concierge"
                      className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                    />
                  )}
                  <div
                    className={`max-w-[75%] p-3 ${
                      message.role === 'user'
                        ? 'bg-[#0F1F3C] text-white'
                        : 'bg-white text-[#0F1F3C] shadow-sm'
                    }`}
                  >
                    {message.role === 'assistant' ? (
                      <div className="prose-chatbot" dir="auto">
                        <ReactMarkdown
                          components={{
                            p: ({node, ...props}) => <p className="mb-2" {...props} />,
                            strong: ({node, ...props}) => <strong className="text-[#D4AF37] font-bold" {...props} />,
                            ul: ({node, ...props}) => <ul className="list-disc ml-4 mb-2" {...props} />,
                            ol: ({node, ...props}) => <ol className="list-decimal ml-4 mb-2" {...props} />,
                            li: ({node, ...props}) => <li className="mb-1" {...props} />
                          }}
                        >
                          {message.content}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <p className="text-sm font-sans whitespace-pre-wrap">{message.content}</p>
                    )}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start items-end space-x-2">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_frenchhotelscape/artifacts/a1c0cpip_image.png" 
                    alt="Concierge"
                    className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                  />
                  <div className="bg-white text-[#0F1F3C] p-3 shadow-sm">
                    <div className="flex items-center space-x-2">
                      <Loader2 className="w-4 h-4 animate-spin text-[#D4AF37]" />
                      <span className="text-sm font-sans text-[#0F1F3C]/60">{t('chat.thinking')}</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Booking Form Inline */}
              {showBookingForm && (
                <div className="px-2 py-2">
                  <BookingFormInline
                    extractedData={bookingData}
                    onClose={() => {
                      console.log('🔴 Closing booking form');
                      setShowBookingForm(false);
                    }}
                    onSuccess={(booking) => {
                      console.log('✅ Booking success:', booking);
                      setShowBookingForm(false);
                      setMessages(prev => [...prev, {
                        role: 'assistant',
                        content: `✅ Booking confirmed! Redirecting to payment...`
                      }]);
                    }}
                  />
                </div>
              )}
              
              {/* Rating Prompt */}
              {showRatingPrompt && !hasRated && (
                <div className="px-2">
                  <RatingPrompt
                    onSubmit={submitRating}
                    onClose={closeRatingPrompt}
                    triggerType={ratingTrigger}
                  />
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 bg-white border-t border-gray-100">
              <div className="flex items-end space-x-2">
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyPress}
                  placeholder={t('chat.placeholder')}
                  rows={1}
                  className="flex-1 bg-[#F9F8F6] border-none px-4 py-3 text-sm font-sans text-[#0F1F3C] placeholder:text-[#0F1F3C]/40 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/20 resize-none overflow-y-auto min-h-[48px] max-h-[120px]"
                  style={{ height: 'auto' }}
                  data-testid="chatbot-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || isLoading}
                  className="w-12 h-12 flex-shrink-0 bg-[#D4AF37] text-white flex items-center justify-center hover:bg-[#B5952F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="chatbot-send"
                  title="Ctrl+Enter to send"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
              <p className="text-xs text-[#0F1F3C]/40 mt-2 font-sans">Press Ctrl+Enter to send</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
